import { Card, Checkbox, Chip, FormControlLabel } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React from "react";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { CampaignFormData } from "@/components/screens/CampaignFormPage/types";
import { validateButtonLink } from "@/helpers/url-validation";
import { CampaignFormPageMessages } from "@/messages/campaigns/campaign-form-page.messages";
import { isFieldDisabled } from "../../constants";
type Props = {
  messages: CampaignFormPageMessages;
};

function SimpleMessageDataTab({ messages }: Props) {
  const { values, handleChange, errors, setFieldValue } = useFormikContext<CampaignFormData>();

  const extractVariableIdentifier = (rawName: string): string => {
    const match = rawName.match(/\{\s*([A-Za-z0-9_]+)\s*:/);
    if (match && match[1]) {
      const identifier = match[1];
      const customerVariableMapping: Record<string, string> = {
        customer_fantasy: messages.customerFantasyVariable,
        customer_city: messages.customerCityVariable,
        contact_name: messages.contactNameVariable,
        contact_city: messages.contactCityVariable,
      };
      if (customerVariableMapping[identifier]) return customerVariableMapping[identifier];
      return identifier;
    }
    return rawName.replace(/[{}]/g, "").split(":")[0].trim();
  };

  const uniqueByKey = <T extends { key: string }>(arr: T[]): T[] => {
    const map = new Map<string, T>();
    arr.forEach((it) => {
      if (!map.has(it.key)) map.set(it.key, it);
    });
    return Array.from(map.values());
  };

  // Lista de variáveis globais com rótulos amigáveis para exibição nos botões
  const globalVariables = [
    { key: messages.customerFantasyVariable, label: messages.customerFantasyLabel },
    { key: messages.customerCityVariable, label: messages.customerCityLabel },
    { key: messages.contactNameVariable, label: messages.contactNameLabel },
    { key: messages.contactCityVariable, label: messages.contactCityLabel },
  ];

  const handleHasButtonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setFieldValue("hasButton", checked);
    if (!checked) {
      setFieldValue("buttonText", "");
      setFieldValue("buttonLink", "");
    }
  };

  const handleButtonLinkChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFieldValue("buttonLink", value);
    if (value && !validateButtonLink(value)) {
      setFieldValue("buttonLinkError", messages.buttonLinkInvalidError);
    } else {
      setFieldValue("buttonLinkError", "");
    }
  };

  const handleInsertVariable = (variableName: string) => {
    const fieldId = "simpleMessage";
    const input = document.getElementById(fieldId) as HTMLInputElement | HTMLTextAreaElement | null;
    const token = `#${variableName}#`;
    if (!input) {
      setFieldValue("simpleMessage", (values.simpleMessage || "") + token);
      return;
    }
    const start = (input as HTMLInputElement).selectionStart || 0;
    const end = (input as HTMLInputElement).selectionEnd || 0;
    const current = values.simpleMessage || "";
    const next = current.slice(0, start) + token + current.slice(end);
    setFieldValue("simpleMessage", next);
    setTimeout(() => {
      try {
        const position = start + token.length;
        (input as HTMLInputElement).setSelectionRange(position, position);
        input.focus();
      } catch {}
    }, 0);
  };

  const getOptionalString = (v: unknown): string | undefined =>
    typeof v === "string" ? v : undefined;
  const buttonLinkError = getOptionalString(
    (values as unknown as Record<string, unknown>).buttonLinkError
  );

  const simpleMessageLength = (values.simpleMessage || "").length;
  const simpleMessageRemaining = 1024 - simpleMessageLength;
  const simpleMessageHelperText =
    `${messages.variablesHelpText} — ` +
    (simpleMessageRemaining === 0
      ? messages.simpleMessageMaxLength
      : `${simpleMessageRemaining} ${messages.simpleMessageCharactersRemaining || "caracteres restantes."}`);

  return (
    <MDBox mt={4}>
      <Card>
        <MDBox p={3}>
          <MDBox display="flex" justifyContent="space-between">
            <MDTypography variant="h5" fontWeight="bold">
              {messages.simpleMessageDataSection}
            </MDTypography>
          </MDBox>

          <MDBox mt={1}>
            <Grid container spacing={3}>
              <Grid size={12}>
                <MDTypography variant="subtitle2" mb={1}>
                  {messages.globalVariablesTitle || "Variáveis Globais"}
                </MDTypography>
                {globalVariables.length > 0 ? (
                  <Grid container sx={{ display: "flex", gap: 1 }}>
                    {globalVariables.map(({ key, label }) => (
                      <Chip
                        key={key}
                        label={label}
                        onClick={() => handleInsertVariable(key)}
                        disabled={isFieldDisabled(values, "simpleMessage")}
                      />
                    ))}
                  </Grid>
                ) : (
                  <MDTypography variant="button" color="text" mt={1}>
                    {messages.noVariablesAvailable || "Nenhuma variável disponível"}
                  </MDTypography>
                )}
              </Grid>

              <Grid size={12}>
                <FormField
                  externalLabel
                  fullWidth
                  name="simpleMessage"
                  id="simpleMessage"
                  type="text"
                  variant="standard"
                  label={messages.simpleMessageLabel}
                  placeholder={messages.simpleMessagePlaceholder}
                  value={values.simpleMessage || ""}
                  onChange={handleChange}
                  multiline
                  rows={6}
                  error={!!errors.simpleMessage}
                  helperText={errors.simpleMessage || simpleMessageHelperText}
                  inputProps={{ maxLength: 1024 }}
                  autoComplete="off"
                  disabled={isFieldDisabled(values, "simpleMessage")}
                />
                <MDTypography variant="caption" color="text" mt={1}>
                  {messages.simpleMessageClarityGuideline || "Mantenha o texto claro e objetivo."}
                </MDTypography>
              </Grid>

              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.hasButton || false}
                      onChange={handleHasButtonChange}
                      name="hasButton"
                      color="primary"
                      disabled={isFieldDisabled(values, "hasButton")}
                    />
                  }
                  label={messages.addButtonLabel}
                />
              </Grid>

              {values.hasButton ? (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormField
                      fullWidth
                      name="buttonText"
                      id="buttonText"
                      type="text"
                      variant="standard"
                      label={messages.buttonTextLabel}
                      placeholder={messages.buttonTextPlaceholder}
                      value={values.buttonText || ""}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.buttonText}
                      helperText={errors.buttonText}
                      inputProps={{ maxLength: 20 }}
                      autoComplete="off"
                      disabled={isFieldDisabled(values, "buttonText")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormField
                      fullWidth
                      name="buttonLink"
                      id="buttonLink"
                      type="url"
                      variant="standard"
                      label={messages.buttonLinkLabel}
                      placeholder={messages.buttonLinkPlaceholder}
                      value={values.buttonLink || ""}
                      onChange={handleButtonLinkChange}
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.buttonLink || !!buttonLinkError}
                      helperText={buttonLinkError || (errors.buttonLink as string | undefined)}
                      autoComplete="url"
                      disabled={isFieldDisabled(values, "buttonLink")}
                    />
                    <MDTypography variant="caption" color="text" mt={1}>
                      {messages.buttonLinkOptionalGuideline}
                    </MDTypography>
                    <MDTypography variant="caption" color="text" mt={1}>
                      {messages.buttonLinkShortenerGuideline ||
                        "Não use encurtadores de URL (ex.: bit.ly, tinyurl, t.co)."}
                    </MDTypography>
                  </Grid>
                </>
              ) : null}
            </Grid>
          </MDBox>
        </MDBox>
      </Card>
    </MDBox>
  );
}

export default SimpleMessageDataTab;
